import { useMutation, useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AssessmentDeliveryState,
  AssessmentDeliveryTemplateFragment,
  AssessmentGroupDelivery,
  AssessmentType,
  ComplianceProgramParticipantStatus,
  GetAssessmentDeliveryQuery,
  GetAssessmentDeliveryQueryVariables,
  SaveAssessmentDeliveryMutation,
  SaveAssessmentDeliveryMutationVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import {
  assessmentTypeSectionHeadings,
  getHiddenQuestionCount,
} from "@apps/assessments/helpers";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  previousUrlVar,
  successNotificationVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import useDebounce from "@components/use-debounce/use-debounce";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertNonNull,
  getUrlUsingTypename,
} from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import getAssessmentDelivery from "../../graphql/get-assessment-delivery";
import updateAssessmentDeliveryMutation from "../../graphql/update-assessment-delivery-mutation";
import AssessmentLayoutSidebar from "../assessment-layout-sidebar";
import AssessmentDeliveryHeader from "./assessment-delivery-header";
import AssessmentDeliveryStack from "./assessment-delivery-stack";

const AssessmentDeliveryActions = ({
  assessmentDeliveryData,
  isSaving,
  assessmentDeliveryId,
  backUrl,
  summary,
  onUpdateDeliveryData,
}: {
  assessmentDeliveryData: NonNullable<GetAssessmentDeliveryQuery>;
  isSaving: boolean;
  assessmentDeliveryId: number;
  backUrl?: string;
  summary: JSON | string | null;
  onUpdateDeliveryData: (data: Partial<DeliveryData>) => void;
}) => {
  const label = useLabel();
  const link = useLink();
  const canUpdate = useMemo(
    () =>
      assessmentDeliveryData?.assessmentDelivery &&
      assessmentDeliveryData.assessmentDelivery.canUpdate?.permission,
    [assessmentDeliveryData],
  );

  const [saveAssessmentDelivery] = useMutation<
    SaveAssessmentDeliveryMutation,
    SaveAssessmentDeliveryMutationVariables
  >(updateAssessmentDeliveryMutation);

  const {
    ConfirmationDialog: DeliverConfirmationDialog,
    confirm: confirmDeliver,
  } = useConfirm(
    "Are you sure?",
    `This will share the responses you have selected to ${assessmentDeliveryData?.assessmentDelivery?.target?.name}. No further responses will be gathered. Do you want to continue?`,
  );

  const handleClickDeliver = useCallback(async () => {
    const confirmation = await confirmDeliver();
    if (confirmation) {
      saveAssessmentDelivery({
        variables: {
          assessmentDeliveryId,
          state: AssessmentDeliveryState.Delivered,
        },
        onCompleted: () => {
          successNotificationVar({
            title: `${label("review", {
              capitalize: true,
            })} package shared`,
          });
          if (backUrl) {
            link.redirect(backUrl);
          }
        },
        refetchQueries: [getAssessmentDelivery],
      });
    }
  }, [
    confirmDeliver,
    saveAssessmentDelivery,
    assessmentDeliveryId,
    label,
    link,
    backUrl,
  ]);

  const {
    ConfirmationDialog: AdminApprovalConfirmationDialog,
    confirm: confirmAdminApproval,
  } = useConfirm(
    "Are you sure?",
    `Proceeding will approve this delivery package and allow the manager of ${assessmentDeliveryData?.assessmentDelivery?.target?.name} to share it whenever they choose. No further responses will be gathered. Do you want to continue?`,
  );
  const handleClickApproveAdminApproval = useCallback(async () => {
    const confirmation = await confirmAdminApproval();
    if (confirmation) {
      saveAssessmentDelivery({
        variables: {
          assessmentDeliveryId,
          state: AssessmentDeliveryState.Draft,
        },
        onCompleted: () => {
          successNotificationVar({
            title: `${label("review", {
              capitalize: true,
            })} package approved`,
          });
          if (backUrl) {
            link.redirect(backUrl);
          }
        },
        refetchQueries: [getAssessmentDelivery],
      });
    }
  }, [
    confirmAdminApproval,
    saveAssessmentDelivery,
    assessmentDeliveryId,
    label,
    link,
    backUrl,
  ]);

  const isDraft =
    assessmentDeliveryData?.assessmentDelivery?.state ===
    AssessmentDeliveryState.Draft;

  const isAwaitingAdminApproval =
    assessmentDeliveryData?.assessmentDelivery?.state ===
    AssessmentDeliveryState.AwaitingAdminApproval;

  const showSummary = (canUpdate && isDraft) || !isEmptyValue(summary);

  return (
    <>
      {showSummary && (
        <div aria-label="Assessment delivery summary">
          <div className="text-gray-500 text-xs uppercase font-semibold">
            Summary
          </div>
          <TextareaWysiwyg
            editable={!!canUpdate && isDraft}
            className="mt-1 bg-white max-w-none"
            value={summary}
            onChangeValue={(newSummary) => {
              onUpdateDeliveryData({
                summary: newSummary,
              });
            }}
          />
        </div>
      )}

      {canUpdate && isDraft && (
        <div className="mt-2 flex items-center justify-end">
          <DeliverConfirmationDialog />
          {isSaving && <Loading className="mr-4" mini size="5" />}
          <Button
            theme={buttonTheme.primary}
            disabled={isSaving}
            onClick={handleClickDeliver}
            text={`Share with ${assessmentDeliveryData?.assessmentDelivery?.target?.name}`}
          />
        </div>
      )}
      {canUpdate && isAwaitingAdminApproval && (
        <div className="mt-2 flex items-center justify-end">
          <AdminApprovalConfirmationDialog />
          {isSaving && <Loading className="mr-4" mini size="5" />}
          <Button
            theme={buttonTheme.primary}
            disabled={isSaving}
            onClick={handleClickApproveAdminApproval}
            text="Approve"
          />
        </div>
      )}
    </>
  );
};

export type DeliveryData = {
  excludedAnswerIds: number[];
  summary: string | null;
};

const AssessmentDelivery = ({
  assessmentDeliveryId,
  headerAvatarReplacement,
  isInSidebar = false,
  isInEmployeeDrawer = false,
}: {
  assessmentDeliveryId: number;
  isInSidebar?: boolean;
  isInEmployeeDrawer?: boolean;
  headerAvatarReplacement?: JSX.Element;
}) => {
  const location = useLocation<TFLocationState>();
  const prevUrl = previousUrlVar();
  const backUrl =
    !isInSidebar && !isInEmployeeDrawer
      ? location.state?.previousPathname || prevUrl || "/assessments"
      : undefined;

  const label = useLabel();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();

  const [proposedDeliveryData, setProposedDeliveryData] =
    useState<DeliveryData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isShowingHiddenQuestions, setIsShowingHiddenQuestions] =
    useState(true);
  const debouncedDeliveryData = useDebounce(proposedDeliveryData, 500);

  const { data: assessmentDeliveryData, loading: isLoadingAssessmentDelivery } =
    useQuery<GetAssessmentDeliveryQuery, GetAssessmentDeliveryQueryVariables>(
      getAssessmentDelivery,
      {
        fetchPolicy: "no-cache",
        variables: {
          assessmentDeliveryId,
          organizationId: organization.id,
        },
        onCompleted: (response) => {
          if (response.assessmentDelivery) {
            const excludedAnswerIds = response.assessmentDelivery
              .excludedAnswerIds as number[];
            setProposedDeliveryData({
              summary: response.assessmentDelivery.summary,
              excludedAnswerIds,
            });
          }
        },
      },
    );

  const isManager =
    assessmentDeliveryData?.assessmentDelivery?.target &&
    assertEdgesNonNull(
      assessmentDeliveryData?.assessmentDelivery?.target?.managers,
    ).some(({ id }) => id === currentUser.id);

  const [saveAssessmentDelivery] = useMutation<
    SaveAssessmentDeliveryMutation,
    SaveAssessmentDeliveryMutationVariables
  >(updateAssessmentDeliveryMutation);

  const canUpdate = useMemo(
    () =>
      assessmentDeliveryData?.assessmentDelivery &&
      assessmentDeliveryData.assessmentDelivery.canUpdate?.permission,
    [assessmentDeliveryData],
  );

  useEffect(() => {
    if (
      !assessmentDeliveryData?.assessmentDelivery ||
      !canUpdate ||
      debouncedDeliveryData === null
    ) {
      return;
    }
    saveAssessmentDelivery({
      variables: {
        assessmentDeliveryId,
        ...debouncedDeliveryData,
      },
      onCompleted: () => setIsSaving(false),
      onError: () => setIsSaving(false),
    });
    // autosave, only fire to backend when debounced data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDeliveryData]);

  const program = useMemo(
    () =>
      assessmentDeliveryData?.assessmentDelivery
        ? assessmentDeliveryData.assessmentDelivery?.complianceProgram
        : null,
    [assessmentDeliveryData],
  );

  const target = useMemo(
    () =>
      assessmentDeliveryData?.assessmentDelivery
        ? assessmentDeliveryData.assessmentDelivery?.target
        : null,
    [assessmentDeliveryData],
  );
  const targetParticipantStatus = useMemo(() => {
    return assessmentDeliveryData?.assessmentDelivery?.complianceProgram?.participantStatus.edges.find(
      (edge) => edge?.node?.user.id === target?.id,
    );
  }, [target, assessmentDeliveryData]);
  const relatedMeetings = assessmentDeliveryData?.assessmentDelivery
    ?.relatedMeetings
    ? assertEdgesNonNull(
        assessmentDeliveryData?.assessmentDelivery?.relatedMeetings,
      )
    : [];

  const viewAsDropdown = useMemo(
    () => (
      <Select
        aria-label="Delivery perspective dropdown"
        prefix="View as"
        options={[
          {
            value: false,
            label: (
              <div className="flex min-w-0 items-center gap-2">
                <div className="shrink-0">
                  <Avatar user={target} size={4} />
                </div>
                <div className="truncate">{`${target?.name} (Subject)`}</div>
              </div>
            ),
          },
          {
            value: true,
            label: (
              <div className="flex min-w-0 items-center gap-2">
                {target?.managers && target.managers.edges.length > 0 ? (
                  <>
                    <div className="shrink-0">
                      <Avatar user={target.managers.edges[0]?.node} size={4} />
                    </div>
                    <div className="truncate">{`${target.managers.edges[0]?.node?.name} (Manager)`}</div>
                  </>
                ) : (
                  <div className="truncate">Manager</div>
                )}
              </div>
            ),
          },
        ]}
        containerClassName={classNames(
          "shrink-0",
          isInEmployeeDrawer && "max-w-[50%]",
        )}
        value={isShowingHiddenQuestions}
        onChange={(showHidden) => setIsShowingHiddenQuestions(showHidden.value)}
      />
    ),
    [isShowingHiddenQuestions, target, isInEmployeeDrawer],
  );

  const handleUpdateDeliveryData = useCallback(
    (data: Partial<DeliveryData>) => {
      if (proposedDeliveryData === null) {
        return;
      }
      setProposedDeliveryData({ ...proposedDeliveryData, ...data });
      setIsSaving(true);
    },
    [proposedDeliveryData],
  );

  const performanceAssessmentTemplate:
    | AssessmentDeliveryTemplateFragment
    | undefined
    | null = useMemo(() => {
    return assessmentDeliveryData?.assessmentDelivery?.complianceProgram
      ?.performanceAssessmentTemplate;
  }, [assessmentDeliveryData]);
  const shouldHavePerformanceAssessmentData = useMemo(() => {
    if (!assessmentDeliveryData?.assessmentDelivery) {
      return false;
    }

    return (
      !!assessmentDeliveryData.assessmentDelivery.complianceProgram
        ?.performanceAssessmentTemplate?.id &&
      (assessmentDeliveryData.assessmentDelivery.complianceProgram
        .performanceAssessmentTemplate.delivery !==
        AssessmentGroupDelivery.None ||
        target?.id !== currentUser.id) &&
      !!targetParticipantStatus &&
      (targetParticipantStatus.node?.performanceAssessmentStatus !==
        ComplianceProgramParticipantStatus.NotApplicable ||
        targetParticipantStatus.node?.performanceSelfAssessmentStatus !==
          ComplianceProgramParticipantStatus.NotApplicable)
    );
  }, [assessmentDeliveryData, targetParticipantStatus, target, currentUser]);
  const managerAssessmentTemplate:
    | AssessmentDeliveryTemplateFragment
    | undefined
    | null = useMemo(() => {
    return assessmentDeliveryData?.assessmentDelivery?.complianceProgram
      ?.managerAssessmentTemplate;
  }, [assessmentDeliveryData]);
  const shouldHaveManagerAssessmentData = useMemo(() => {
    if (!assessmentDeliveryData?.assessmentDelivery) {
      return false;
    }
    return (
      !!assessmentDeliveryData.assessmentDelivery.complianceProgram
        ?.managerAssessmentTemplate?.id &&
      (assessmentDeliveryData.assessmentDelivery.complianceProgram
        .managerAssessmentTemplate.delivery !== AssessmentGroupDelivery.None ||
        target?.id !== currentUser.id) &&
      !!targetParticipantStatus &&
      targetParticipantStatus.node?.managerAssessmentStatus !==
        ComplianceProgramParticipantStatus.NotApplicable
    );
  }, [assessmentDeliveryData, targetParticipantStatus, target, currentUser]);
  const peerAssessmentTemplate:
    | AssessmentDeliveryTemplateFragment
    | undefined
    | null = useMemo(() => {
    if (
      assessmentDeliveryData?.assessmentDelivery?.complianceProgram
        ?.peerAssessmentTemplate?.delivery !== AssessmentGroupDelivery.None ||
      isShowingHiddenQuestions
    ) {
      return assessmentDeliveryData?.assessmentDelivery?.complianceProgram
        ?.peerAssessmentTemplate;
    }
    return undefined;
  }, [assessmentDeliveryData, isShowingHiddenQuestions]);
  const shouldHavePeerAssessmentData = useMemo(() => {
    if (!assessmentDeliveryData?.assessmentDelivery) {
      return false;
    }
    return (
      !!assessmentDeliveryData.assessmentDelivery.complianceProgram
        ?.peerAssessmentTemplate?.id &&
      (assessmentDeliveryData.assessmentDelivery.complianceProgram
        .peerAssessmentTemplate.delivery !== AssessmentGroupDelivery.None ||
        target?.id !== currentUser.id) &&
      !!targetParticipantStatus &&
      (targetParticipantStatus.node?.peerAssessmentStatus !==
        ComplianceProgramParticipantStatus.NotApplicable ||
        targetParticipantStatus.node?.peerSelfAssessmentStatus !==
          ComplianceProgramParticipantStatus.NotApplicable)
    );
  }, [assessmentDeliveryData, targetParticipantStatus, target, currentUser]);

  const hiddenQuestionCount = useMemo(() => {
    let count = 0;
    if (performanceAssessmentTemplate) {
      count += getHiddenQuestionCount(
        performanceAssessmentTemplate,
        assertNonNull(assessmentDeliveryData?.assessmentDelivery),
        performanceAssessmentTemplate.delivery,
      );
    }
    if (managerAssessmentTemplate) {
      count += getHiddenQuestionCount(
        managerAssessmentTemplate,
        assertNonNull(assessmentDeliveryData?.assessmentDelivery),
        managerAssessmentTemplate.delivery,
      );
    }
    if (peerAssessmentTemplate) {
      count += getHiddenQuestionCount(
        peerAssessmentTemplate,
        assertNonNull(assessmentDeliveryData?.assessmentDelivery),
        peerAssessmentTemplate.delivery,
      );
    }
    return count;
  }, [
    performanceAssessmentTemplate,
    managerAssessmentTemplate,
    peerAssessmentTemplate,
    assessmentDeliveryData,
  ]);

  const isDraft =
    assessmentDeliveryData?.assessmentDelivery?.state ===
    AssessmentDeliveryState.Draft;

  return (
    <Layout className="bg-white" absoluteInsetPosition={!isInEmployeeDrawer}>
      {!isInSidebar && !isInEmployeeDrawer && (
        <Layout.Header
          breadcrumbs={compact([
            {
              title: label("review", { capitalize: true, pluralize: true }),
              url: "/assessments",
            },
            assessmentDeliveryData && {
              title: `${
                assessmentDeliveryData.assessmentDelivery?.target?.name
              } ${label("review", { capitalize: true })}: ${
                assessmentDeliveryData.assessmentDelivery?.complianceProgram
                  ?.title || "Untitled"
              }`,
              url: assessmentDeliveryData.assessmentDelivery
                ? (getUrlUsingTypename(
                    assessmentDeliveryData.assessmentDelivery,
                  ) as string)
                : `/assessments`,
            },
          ])}
          sidebarExpandedUiPreferenceKey="assessmentDeliverySidebarExpanded"
          className="bg-white"
        >
          {hiddenQuestionCount > 0 && viewAsDropdown}
          <Dropdown
            className="tf-print-display-none"
            align="end"
            options={[
              {
                label: "Print",
                onClick: () => {
                  window.print();
                },
              },
            ]}
          />
        </Layout.Header>
      )}
      <Layout.Container
        loading={isLoadingAssessmentDelivery}
        noHeader={isInSidebar || isInEmployeeDrawer}
        absoluteInsetPosition={!isInEmployeeDrawer}
      >
        <Layout.Main
          sidebarExpandedUiPreferenceKey={
            isInSidebar || isInEmployeeDrawer
              ? undefined
              : "assessmentDeliverySidebarExpanded"
          }
          width={isInSidebar || isInEmployeeDrawer ? "sidebar" : "md"}
          absoluteInsetPosition={!isInEmployeeDrawer}
          className="print:px-4"
        >
          <div aria-label="Assessments > performance assessment delivery">
            <div className="flex flex-col">
              {assessmentDeliveryData &&
                assessmentDeliveryData.assessmentDelivery &&
                proposedDeliveryData && (
                  <>
                    <AssessmentDeliveryHeader
                      assessmentDeliveryData={assessmentDeliveryData}
                      viewAsDropdown={
                        isInSidebar || isInEmployeeDrawer
                          ? viewAsDropdown
                          : undefined
                      }
                      avatarReplacement={headerAvatarReplacement}
                      defaultCollapsed={isInEmployeeDrawer}
                    />

                    <AssessmentDeliveryStack
                      title={
                        assessmentTypeSectionHeadings(label, true)[
                          AssessmentType.Performance
                        ]
                      }
                      assessmentTemplate={performanceAssessmentTemplate}
                      assessmentDeliveryData={
                        assessmentDeliveryData.assessmentDelivery
                      }
                      isShowingHiddenQuestions={isShowingHiddenQuestions}
                      shouldHaveData={shouldHavePerformanceAssessmentData}
                      onUpdateDeliveryData={handleUpdateDeliveryData}
                      canUpdate={!!canUpdate}
                      isDraft={isDraft}
                      excludedAnswerIds={proposedDeliveryData.excludedAnswerIds}
                    />

                    <AssessmentDeliveryStack
                      title={
                        assessmentTypeSectionHeadings(label, true)[
                          AssessmentType.Manager
                        ]
                      }
                      assessmentTemplate={managerAssessmentTemplate}
                      assessmentDeliveryData={
                        assessmentDeliveryData.assessmentDelivery
                      }
                      isShowingHiddenQuestions={isShowingHiddenQuestions}
                      shouldHaveData={shouldHaveManagerAssessmentData}
                      onUpdateDeliveryData={handleUpdateDeliveryData}
                      canUpdate={!!canUpdate}
                      isDraft={isDraft}
                      excludedAnswerIds={proposedDeliveryData.excludedAnswerIds}
                    />
                    <AssessmentDeliveryStack
                      title={
                        assessmentTypeSectionHeadings(label, true)[
                          AssessmentType.Peer
                        ]
                      }
                      assessmentTemplate={peerAssessmentTemplate}
                      assessmentDeliveryData={
                        assessmentDeliveryData.assessmentDelivery
                      }
                      isShowingHiddenQuestions={isShowingHiddenQuestions}
                      shouldHaveData={shouldHavePeerAssessmentData}
                      onUpdateDeliveryData={handleUpdateDeliveryData}
                      canUpdate={!!canUpdate}
                      isDraft={isDraft}
                      excludedAnswerIds={proposedDeliveryData.excludedAnswerIds}
                    />
                    <AssessmentDeliveryActions
                      assessmentDeliveryData={assessmentDeliveryData}
                      isSaving={isSaving}
                      backUrl={backUrl}
                      assessmentDeliveryId={assessmentDeliveryId}
                      summary={proposedDeliveryData.summary}
                      onUpdateDeliveryData={handleUpdateDeliveryData}
                    />
                  </>
                )}
            </div>
          </div>
        </Layout.Main>
        {!isInSidebar && !isInEmployeeDrawer && target && (
          <AssessmentLayoutSidebar
            target={target}
            periodStartDate={program?.periodStartDate}
            periodEndDate={program?.periodEndDate}
            relatedMeetings={relatedMeetings}
            isManager={!!isManager}
            key={assessmentDeliveryId}
            sidebarExpandedUiPreferenceKey="assessmentDeliverySidebarExpanded"
          />
        )}
      </Layout.Container>
    </Layout>
  );
};

export default AssessmentDelivery;
